import { useState, useEffect } from "react";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip } from "@progress/kendo-react-charts";
import { getMonthlyTurnoverStatisticsBySpeciesApi } from "api/statistic";
import { MonthlyTurnoverStatisticsBySpeciesItem } from "interface/statistic";
import styles from "../../../styles.module.css";
import { useTranslation } from "react-i18next";

interface ChartData {
    month: string;
    [key: string]: string | number;
}

const MonthlyValueBySpeciesCard = ({ isMobileView }: { isMobileView: boolean }) => {
    const { t } = useTranslation();
    const [chartData, setChartData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await getMonthlyTurnoverStatisticsBySpeciesApi(currentYear);
                
                if (response.data) {
                    const transformedData = transformDataForChart(response.data);
                    setChartData(transformedData);
                } else {
                    setError(t("NoDataAvailable"));
                }
            } catch (err: any) {
                console.error("Error fetching monthly value statistics:", err);
                setError(t("FailedToLoadStatistics"));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentYear, t]);

    const transformDataForChart = (
        rawData: MonthlyTurnoverStatisticsBySpeciesItem[]
    ): ChartData[] => {
        const monthlyData: { [key: string]: any } = {};

        rawData.forEach((item) => {
            const monthKey = item.monthName || `Month ${item.month}`;

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    month: monthKey,
                };
            }

            const speciesKey = item.speciesQuotaCode || item.specieCode || "Unknown";
            const value = (item.value || 0);
            monthlyData[monthKey][speciesKey] = (monthlyData[monthKey][speciesKey] || 0) + value;
        });

        // Convert to array and sort by month number
        return Object.values(monthlyData).sort((a, b) => {
            const monthOrder: { [key: string]: number } = {
                Januar: 1,
                Februar: 2,
                Mars: 3,
                April: 4,
                Mai: 5,
                Juni: 6,
                Juli: 7,
                August: 8,
                September: 9,
                Oktober: 10,
                November: 11,
                Desember: 12,
            };
            return (monthOrder[a.month as string] || 0) - (monthOrder[b.month as string] || 0);
        });
    };

    const getSpeciesColors = (species: string[]): { [key: string]: string } => {
        const colors = [
            "#8884d8",
            "#82ca9d",
            "#ffc658",
            "#ff7c7c",
            "#8dd1e1",
            "#d084d0",
            "#82d982",
            "#ffa07a",
        ];
        const colorMap: { [key: string]: string } = {};
        species.forEach((specie, index) => {
            colorMap[specie] = colors[index % colors.length];
        });
        return colorMap;
    };

    if (loading) {
        return (
            <Card style={{ width: "100%" }}>
                <CardBody>
                    <div className={`${styles.BoxHeadingMedium} ${styles.CardBodyHeader}`}>{t("MonthlyValueBySpecies")}</div>
                    <p>{t("Loading")}</p>
                </CardBody>
            </Card>
        );
    }

    if (error || chartData.length === 0) {
        return (
            <Card style={{ width: "100%" }}>
                <CardBody>
                    <div className={`${styles.BoxHeadingMedium} ${styles.CardBodyHeader}`}>{t("MonthlyValueBySpecies")}</div>
                    <p>{error || t("NoDataAvailable")}</p>
                </CardBody>
            </Card>
        );
    }

    const allSpecies = Array.from(
        new Set(
            chartData.flatMap((item) =>
                Object.keys(item).filter((key) => key !== "month")
            )
        )
    );

    const speciesColors = getSpeciesColors(allSpecies);
    const months = chartData.map((item) => item.month);

    return (
        <Card style={{ width: "100%" }}>
            <CardBody>
                <div className={`${styles.BoxHeadingMedium} ${styles.CardBodyHeader}`}>
                    {t("MonthlyValueBySpecies")}
                </div>
                <div style={{ marginTop: "20px" }}>
                    <Chart
                        style={{
                            height: isMobileView ? 300 : 400,
                        }}
                    >
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem
                                categories={months}
                                type="category"
                                labels={{
                                    rotation: isMobileView ? -45 : -60,
                                    padding: 20,
                                }}
                            />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem
                                labels={{
                                    format: "n0",
                                }}
                                title={{
                                    text: t("Value"),
                                }}
                            />
                        </ChartValueAxis>
                        <ChartLegend position="bottom" orientation="horizontal" />
                        <ChartTooltip
                            render={(context: any) => {
                                const seriesName = context.point.series?.name || "";
                                const dataPoint = context.point.dataItem;
                                const value = dataPoint ? parseFloat(dataPoint).toLocaleString("no-NO", { maximumFractionDigits: 2 }) : "0";
                                const category = context.point.category || "";
                                return (
                                    <div style={{ padding: "8px", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", color: "black" }}>
                                        <div>
                                            <strong>{seriesName} - {category}</strong>
                                        </div>
                                        <div>
                                            {value}kr
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <ChartSeries>
                            {allSpecies.map((species) => (
                                <ChartSeriesItem
                                    key={species}
                                    type="line"
                                    name={species}
                                    data={chartData.map((item) => item[species] || 0)}
                                    color={speciesColors[species]}
                                    width={2}
                                    markers={{
                                        visible: true,
                                        size: 4,
                                        border: {
                                            width: 1,
                                        },
                                    }}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                </div>
            </CardBody>
        </Card>
    );
};

export default MonthlyValueBySpeciesCard;